import React, { useRef } from "react";
import { useCountUp } from "../../hooks/useCountUp";
import { useInView } from "../../hooks/useInView";

// =============================================================================
// <CountUp> — drop-in count-up for any rendered number, scroll-revealed.
// =============================================================================
// Replaces `{formatX(value)}` with `<CountUp value={value} format={formatX} />`.
// The formatter runs every frame over the tweened value; the final frame is
// exactly `value`, so the settled output matches the old static render 1:1.
//
// The tween HOLDS until the number scrolls into view (own one-shot
// IntersectionObserver on the rendered <span>), so numbers count up as Mako
// scrolls instead of all firing invisibly at page load. Above-fold numbers
// fire immediately — the observer resolves on the first frame. Under
// prefers-reduced-motion the value renders settled regardless of visibility
// (keeps below-fold one-shot innerText() spec reads deterministic).
//
// Pass `delay` to stagger alongside .anim-fill bars (see /animate skill).
//
// Only mount with a real finite number — keep null/"—" fallbacks OUTSIDE:
//   {mins != null ? <CountUp value={mins} format={formatMinutes} /> : "—"}
// =============================================================================

export interface CountUpProps {
  value: number;
  /** Formatter applied each frame. Default: rounded integer string. */
  format?: (n: number) => string;
  /** Animation length in ms (default 900). */
  duration?: number;
  /** Stagger delay in ms before the tween starts. */
  delay?: number;
}

const defaultFormat = (n: number) => Math.round(n).toString();

export const CountUp: React.FC<CountUpProps> = ({
  value,
  format = defaultFormat,
  duration,
  delay,
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref);
  const animated = useCountUp(value, { duration, delay, start: inView });
  return <span ref={ref}>{format(animated)}</span>;
};

export default CountUp;
